import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";

import { BOARDBOOK_DATASET } from "./boardbook-sync-codec";

/** Minimal browser-WebSocket surface, overridable in tests. */
export interface WebSocketLike {
  readyState: number;
  /** Bytes sent but not yet on the wire; absent on test fakes. */
  readonly bufferedAmount?: number;
  send(data: string): void;
  close(): void;
  onopen: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: (() => void) | null;
  onerror: (() => void) | null;
}

/** Connection target for the boardbook kernel room. */
export interface BoardbookSyncTarget {
  url: string;
  token: string;
  scope: string;
  tenant: string;
}

/**
 * Trust gate for opening the kernel socket: returns a target only when the
 * endpoint, the auth token, and the registration scope are all present.
 * A missing piece yields null — the caller must not open a half-configured
 * socket (no token = rejected handshake; no scope = wrong/empty room).
 */
export function boardbookSyncTarget(input: {
  url?: string | null;
  token?: string | null;
  scope?: string | null;
  tenant?: string | null;
}): BoardbookSyncTarget | null {
  const url = input.url ?? "";
  const token = input.token ?? "";
  const scope = input.scope ?? "";
  const tenant = input.tenant ?? "";
  if (!url || !token || !scope || !tenant) return null;
  return { url, token, scope, tenant };
}

/** One outbound row op (per-entry: uuid = LogEntry.id, or the legacy book uuid). */
interface ApplyOp {
  dataset: string;
  scope: string;
  uuid: string;
  type: "upsert" | "delete";
  updatedAt: number;
  body?: unknown;
}

/** One inbound row op the kernel replayed/broadcast, forwarded verbatim for the
 * service to fold (it owns the codec + local DB, this stays a pure transport). */
export interface RemoteRowOp {
  tenant: string;
  /** Scope of the socket the op arrived on. */
  scope: string;
  uuid: string;
  type: "upsert" | "delete";
  updatedAt: number;
  body?: unknown;
  /** Fleetplan state of an upserted row, when the kernel sent one (catch-up and lifecycle changes). */
  lifecycle?: string | null;
}

export interface SettledOp {
  tenant: string;
  scope: string;
  uuid: string;
  updatedAt: number;
  outcome: "ack" | "refused" | "retry";
}

const OPEN = 1;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;
const BACKOFF_EXP_CAP = 15;

/**
 * Kernel-dialect WebSocket client for the boardbook dataset (per-entry rows).
 *
 * Speaks the efb-sync-server wire directly — `SUBSCRIBE{dataset,scope,sinceSeq}`,
 * `APPLY{ops:[ApplyOp]}`, inbound `REMOTE{ops:[RemoteOp]}` / `SYNCED`. Scoped to
 * one (dataset=`boardbook`, scope=registration) room, but that room now holds
 * MANY rows (one uuid per log entry). This client is a pure transport: it forwards
 * every row op (`remoteOps$`) and advances the resume cursor; per-entry LWW and
 * the body codec live in the service.
 *
 * The token rides the `?token=` query string: the WebView/browser WebSocket API
 * can't set an `Authorization` header, so the kernel endpoint (or its proxy) must
 * accept the query token. Token-expiry is gated by the caller (LogbookService).
 *
 * Buffers unsent ops in FIFO order until the socket is open (per-entry ops must
 * not collapse — two different entries are two rows); reconnects with backoff and
 * resubscribes from the highest `serverSeq` seen. `connect()` re-handshakes when
 * the url/token/room change and resets the resume cursor on a room change so it
 * never serves the wrong room.
 */
@Injectable({ providedIn: "root" })
export class BoardbookKernelClient {
  socketFactory: (url: string) => WebSocketLike = (url) => new WebSocket(url) as unknown as WebSocketLike;

  private socket: WebSocketLike | null = null;
  private url = "";
  private token = "";
  private scope = "";
  private tenant = "";
  private wants = false;
  private sinceSeq = 0;
  /** The kernel's id from the last SYNCED. It changes when the kernel restarts or its store is
   *  restored, and a restored store can reuse serverSeq values, so the cursor is then void. */
  private storeId: string | null = null;
  /** A replay from 0 has not reached SYNCED since start, a room change or a store change, so a
   *  reconnect subscribes from 0 again instead of from the partial cursor. */
  private replayOwed = true;
  /** FIFO outbound queue — each entry is a distinct row, so ops must not collapse. */
  private pending: ApplyOp[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;

  private readonly _remoteOps$ = new Subject<RemoteRowOp>();
  /** Live per-row ops from the kernel (replay + broadcast). Not a BehaviorSubject:
   * the local DB is the source of truth on (re)subscribe, so there is no snapshot
   * to replay — the service folds each op as it arrives. */
  readonly remoteOps$: Observable<RemoteRowOp> = this._remoteOps$.asObservable();

  private readonly _refused$ = new Subject<{ uuid: string; reason: string }>();
  /** Ops the kernel refused for good: it holds a newer ('lww') or sealed ('immutable') row, or the
   * row waits for the Fleetplan sender and keeps its delete ('queued'). */
  readonly refused$: Observable<{ uuid: string; reason: string }> = this._refused$.asObservable();

  private readonly _synced$ = new Subject<{ tenant: string; scope: string }>();
  /** Emitted at every SYNCED (full or delta replay). */
  readonly synced$: Observable<{ tenant: string; scope: string }> = this._synced$.asObservable();

  private readonly _settled$ = new Subject<SettledOp>();
  /** Every answered op, with the stamp of the op itself. */
  readonly settled$: Observable<SettledOp> = this._settled$.asObservable();

  private readonly _fullReplay$ = new Subject<{ tenant: string; scope: string; uuids: Set<string> }>();
  /** Uuids named by a replay that started at sinceSeq 0, emitted at SYNCED. */
  readonly fullReplay$: Observable<{ tenant: string; scope: string; uuids: Set<string> }> =
    this._fullReplay$.asObservable();

  /** Sent ops awaiting their ACK or REJECT, FIFO per uuid (replies come in op order). */
  private inFlight = new Map<string, ApplyOp[]>();
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryAttempt = 0;
  private replayed: Set<string> | null = null;

  private settledResolve: (() => void) | null = null;
  private settledP: Promise<void> = Promise.resolve();

  /** Resolves once the current subscription has replayed (`SYNCED`); already
   * resolved before any connect or after catch-up. */
  settled(): Promise<void> {
    return this.settledP;
  }

  /** Ops a page reload would lose: queued while the socket is down or the token is
   *  expired (memory only), or sent and still in the socket buffer. */
  get pendingCount(): number {
    let inFlight = 0;
    for (const list of this.inFlight.values()) inFlight += list.length;
    return this.pending.length + inFlight + (this.socket?.bufferedAmount ? 1 : 0);
  }

  /** The room this client is bound to, `<tenant>:<scope>`; empty when not bound. */
  get room(): string {
    return this.scope ? `${this.tenant}:${this.scope}` : "";
  }

  connect(opts: BoardbookSyncTarget): void {
    const scopeChanged = opts.scope !== this.scope;
    const tenant = opts.tenant;
    const roomChanged = scopeChanged || tenant !== this.tenant;
    const configChanged = opts.url !== this.url || opts.token !== this.token || roomChanged;
    if (roomChanged && this.scope) {
      // No op crosses rooms.
      this.pending = [];
      this.inFlight.clear();
      this.clearRetry();
      this.retryAttempt = 0;
    }
    this.tenant = tenant;
    this.url = opts.url;
    this.token = opts.token;
    this.scope = opts.scope;
    this.wants = true;

    if (roomChanged) {
      // New room — the kernel numbers serverSeq per tenant and scope.
      this.sinceSeq = 0;
      this.replayOwed = true;
    }
    if (configChanged) {
      // Force a fresh handshake under the new url/token/scope.
      this.dropSocket();
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
        this.attempt = 0;
      }
    }
    this.open();
  }

  disconnect(): void {
    this.wants = false;
    this.clearRetry();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.dropSocket();
  }

  pushUpsert(uuid: string, updatedAt: number, body: unknown): void {
    this.queue({ dataset: BOARDBOOK_DATASET, scope: this.scope, uuid, type: "upsert", updatedAt, body });
  }

  pushDelete(uuid: string, updatedAt: number): void {
    this.queue({ dataset: BOARDBOOK_DATASET, scope: this.scope, uuid, type: "delete", updatedAt });
  }

  private queue(op: ApplyOp): void {
    // Coalesce a superseding op for the SAME uuid (only the latest state of one
    // row matters), but keep distinct-uuid ops so no entry is dropped.
    this.pending = this.pending.filter((p) => p.uuid !== op.uuid);
    this.pending.push(op);
    this.flush();
  }

  private open(): void {
    if (this.socket || this.reconnectTimer || !this.url || !this.token || !this.scope) return;
    const sock = this.socketFactory(`${this.url}?token=${encodeURIComponent(this.token)}`);
    this.socket = sock;
    this.armSettled();
    sock.onopen = () => {
      this.attempt = 0;
      if (this.replayOwed) this.sinceSeq = 0;
      this.replayed = this.sinceSeq === 0 ? new Set() : null;
      this.send({ type: "SUBSCRIBE", dataset: BOARDBOOK_DATASET, scope: this.scope, sinceSeq: this.sinceSeq });
      this.flush();
    };
    sock.onmessage = (ev) => this.onMessage(ev.data);
    sock.onclose = () => this.onClose();
    sock.onerror = () => {
      /* a close event follows; reconnect is driven from there */
    };
  }

  /** Detach handlers + close without scheduling a reconnect (deliberate teardown). */
  private dropSocket(): void {
    const sock = this.socket;
    this.socket = null;
    this.clearRetry();
    this.requeueInFlight();
    if (!sock) return;
    sock.onopen = null;
    sock.onmessage = null;
    sock.onclose = null;
    sock.onerror = null;
    sock.close();
  }

  private onClose(): void {
    this.socket = null;
    this.requeueInFlight();
    if (!this.wants || this.reconnectTimer) return;
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** Math.min(this.attempt++, BACKOFF_EXP_CAP));
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private onMessage(data: unknown): void {
    let msg: { type?: string; ops?: unknown };
    try {
      msg = JSON.parse(String(data));
    } catch {
      return;
    }
    if (msg.type === "ACK" || msg.type === "REJECT") {
      const m = msg as { uuid?: unknown; reason?: unknown; terminal?: unknown };
      const uuid = typeof m.uuid === "string" ? m.uuid : "";
      const list = this.inFlight.get(uuid);
      const op = list?.shift();
      if (list && !list.length) this.inFlight.delete(uuid);
      if (msg.type === "ACK") this.retryAttempt = 0;
      if (op) {
        const outcome = msg.type === "ACK" ? "ack" : m.terminal === false ? "retry" : "refused";
        this._settled$.next({ tenant: this.tenant, scope: this.scope, uuid, updatedAt: op.updatedAt, outcome });
      }
      if (msg.type === "REJECT") {
        if (m.terminal === false) {
          if (op && !this.inFlight.has(uuid) && !this.pending.some((p) => p.uuid === uuid)) this.pending.push(op);
          this.scheduleRetry();
        } else if (m.reason === "lww" || m.reason === "immutable" || m.reason === "queued") {
          this._refused$.next({ uuid, reason: m.reason });
        }
      }
      return;
    }
    if (msg.type === "SYNCED") {
      const storeId = (msg as { storeId?: unknown }).storeId;
      if (typeof storeId === "string") {
        const changed = this.storeId !== null && storeId !== this.storeId;
        this.storeId = storeId;
        if (changed && !this.replayed) {
          // Replay from 0: the full replay resends every unsealed row the kernel no longer has.
          this.replayOwed = true;
          this.sinceSeq = 0;
          this.replayed = null;
          this.dropSocket();
          this.open();
          return;
        }
      }
      if (this.replayed) {
        const named = this.replayed;
        this.replayed = null;
        this.replayOwed = false;
        this._fullReplay$.next({ tenant: this.tenant, scope: this.scope, uuids: named });
      }
      this._synced$.next({ tenant: this.tenant, scope: this.scope });
      this.settledResolve?.();
      this.settledResolve = null;
      return;
    }
    if (msg.type !== "REMOTE" || !Array.isArray(msg.ops)) return;

    for (const raw of msg.ops) {
      const op = raw as {
        type?: string;
        uuid?: unknown;
        serverSeq?: unknown;
        updatedAt?: unknown;
        body?: unknown;
        lifecycle?: unknown;
      };
      const seq = Number(op.serverSeq);
      // Advance the cursor for EVERY op in the stream so we never re-replay.
      if (Number.isFinite(seq) && seq > this.sinceSeq) this.sinceSeq = seq;
      const uuid = typeof op.uuid === "string" ? op.uuid : "";
      const updatedAt = Number(op.updatedAt);
      if (!uuid || !Number.isFinite(updatedAt)) continue;
      this.replayed?.add(uuid);
      if (op.type === "upsert" || op.type === "delete") {
        const lc = op.lifecycle;
        const lifecycle: string | null | undefined = typeof lc === "string" ? lc : lc === null ? null : undefined;
        this._remoteOps$.next({
          tenant: this.tenant,
          scope: this.scope,
          uuid,
          type: op.type,
          updatedAt,
          body: op.body,
          ...(op.type === "upsert" && lifecycle !== undefined ? { lifecycle } : {}),
        });
      }
    }
  }

  private armSettled(): void {
    this.settledP = new Promise<void>((resolve) => {
      this.settledResolve = resolve;
    });
  }

  private flush(): void {
    if (!this.pending.length || !this.socket || this.socket.readyState !== OPEN) return;
    const ops = this.pending;
    this.pending = [];
    for (const op of ops) {
      const list = this.inFlight.get(op.uuid);
      if (list) list.push(op);
      else this.inFlight.set(op.uuid, [op]);
    }
    this.send({ type: "APPLY", ops });
  }

  private scheduleRetry(): void {
    if (this.retryTimer) return;
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.retryAttempt++);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.flush();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private requeueInFlight(): void {
    const back: ApplyOp[] = [];
    for (const list of this.inFlight.values()) {
      const newest = list[list.length - 1];
      if (newest && !this.pending.some((p) => p.uuid === newest.uuid)) back.push(newest);
    }
    this.inFlight.clear();
    this.pending = [...back, ...this.pending];
  }

  private send(msg: unknown): void {
    if (this.socket && this.socket.readyState === OPEN) this.socket.send(JSON.stringify(msg));
  }
}
